import React, { useState, useCallback, memo } from "react";
import ButtonTertiary from "shared/Button/ButtonTertiary";
import Checkbox from "shared/Checkbox/Checkbox";
import ToTitleCase from "utils/ToTitleCase";
import infiniteLoader from "../assets/loaders/InfinityLoader.gif";
import { toast } from "react-hot-toast";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { ISidebarFilters } from "Interfaces/interface.filter";
import { IFacetItem, IFacetValue } from "Interfaces/interface.facet";
import { ChevronDown } from "lucide-react";
import { ICategory } from "@/Interfaces/interface.productsapi";
import { useAppSelector } from "@/store/hooks/hooks";
import { selectProductStatus } from "@/store/slices/productsSlice";

interface ExtendedProps extends ISidebarFilters {
  selectedCategoryIds: string[];
  selectedSubCategoryIds: string[];
  selectedBrandIds: string[];
  onCategoryChange: (ids: string[]) => void;
  onSubCategoryChange: (ids: string[]) => void;
  onBrandChange: (ids: string[]) => void;
}

const DesktopFilterSidebar: React.FC<ExtendedProps> = ({
  clearFilter,
  showClear,
  filterData,
  handleFilter,
  facets = [],
  loading,
  categories,
  selectedCategoryIds,
  selectedSubCategoryIds,
  selectedBrandIds,
  onCategoryChange,
  onSubCategoryChange,
  onBrandChange,
}) => {
  const [expandedFacets, setExpandedFacets] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [priceError, setPriceError] = useState("");

  const productsLoading = useAppSelector(selectProductStatus);
  const checkDisable = productsLoading === "loading";

  const validatePriceRange = useCallback((min: string, max: string) => {
    if (!min || !max) {
      setPriceError("");
      return false;
    }

    const minNum = Number(min);
    const maxNum = Number(max);

    if (minNum < 0 || maxNum < 0) {
      setPriceError("Prices cannot be negative");
      return false;
    }

    if (minNum > maxNum) {
      setPriceError("Minimum price cannot be greater than maximum price");
      return false;
    }

    if (minNum === maxNum) {
      setPriceError("Minimum and maximum prices cannot be the same");
      return false;
    }

    setPriceError("");
    return true;
  }, []);

  const toggleExpanded = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const handleMinPriceChange = useCallback(
    (value: string) => {
      if (value.length > 5) {
        toast.error("Price cannot exceed 5 digits");
        return;
      }

      setMinPrice(value);
      validatePriceRange(value, maxPrice);
    },
    [maxPrice, validatePriceRange]
  );

  const handleMaxPriceChange = useCallback(
    (value: string) => {
      if (value.length > 5) {
        toast.error("Price cannot exceed 5 digits");
        return;
      }

      setMaxPrice(value);
      validatePriceRange(minPrice, value);
    },
    [minPrice, validatePriceRange]
  );

  function isCategory(value: IFacetValue | ICategory): value is ICategory {
    return (value as ICategory).sub_category !== undefined;
  }

  const handleSelectionChange = useCallback(
    (
      facetName: string,
      selectedValue: IFacetValue | ICategory,
      isChecked: boolean
    ) => {
      const lowerFacet = facetName.toLowerCase();

      if (lowerFacet === "category") {
        const newCategoryIds = isChecked
          ? [...new Set([...selectedCategoryIds, selectedValue.id])]
          : selectedCategoryIds.filter((id) => id !== selectedValue.id);

        onCategoryChange(newCategoryIds);

        const thisCat = selectedValue as ICategory;

        if (isChecked) {
          const subsToRemove = thisCat.sub_category?.map((s) => s.id) || [];
          const newSubIds = selectedSubCategoryIds.filter(
            (id) => !subsToRemove.includes(id)
          );
          onSubCategoryChange(newSubIds);
        } else {
          const subsToRemove = thisCat.sub_category?.map((s) => s.id) || [];
          const newSubIds = selectedSubCategoryIds.filter(
            (id) => !subsToRemove.includes(id)
          );
          onSubCategoryChange(newSubIds);
        }
      } else if (lowerFacet === "subcategory") {
        const newSubCategoryIds = isChecked
          ? [...new Set([...selectedSubCategoryIds, selectedValue.id])]
          : selectedSubCategoryIds.filter((id) => id !== selectedValue.id);

        onSubCategoryChange(newSubCategoryIds);

        if (isChecked) {
          const categoryFacet = facets.find(
            (f) => f.name.toLowerCase() === "category"
          );
          const parentCategory = categoryFacet?.values.find((cat) => {
            if (isCategory(cat)) {
              return cat.sub_category?.some((s) => s.id === selectedValue.id);
            }
            return false;
          }) as ICategory | undefined;

          if (parentCategory) {
            const updatedCategoryIds = selectedCategoryIds.filter(
              (id) => id !== parentCategory.id
            );
            onCategoryChange(updatedCategoryIds);
          }
        }
      } else if (lowerFacet === "brand") {
        const newBrandIds = isChecked
          ? [...new Set([...selectedBrandIds, selectedValue.id])]
          : selectedBrandIds.filter((id) => id !== selectedValue.id);
        onBrandChange(newBrandIds);
      } else {
        handleFilter(facetName, selectedValue, isChecked ? "add" : "remove");
      }
    },
    [
      facets,
      handleFilter,
      selectedCategoryIds,
      selectedSubCategoryIds,
      selectedBrandIds,
      onCategoryChange,
      onSubCategoryChange,
      onBrandChange,
    ]
  );

  const CheckBoxGroup = ({
    item,
    showAll,
    onToggleShowAll,
  }: {
    item: IFacetItem;
    showAll: boolean;
    onToggleShowAll: () => void;
  }) => {
    // Compute checked count
    let checkedCount = 0;
    if (item.name.toLowerCase() === "category") {
      checkedCount = item.values.filter(
        (val: ICategory) =>
          filterData.includes(val.id) ||
          val.sub_category?.some((sub) => filterData.includes(sub.id))
      ).length;
    } else {
      checkedCount = item.values.filter((val) =>
        filterData.includes(val.id)
      ).length;
    }
    const maxCheckboxesToShow = checkedCount > 2 ? checkedCount : 2;

    // Sort values: checked first
    let displayValues = item.values;
    if (item.name.toLowerCase() === "category") {
      displayValues = [...(item.values as ICategory[])].sort((a, b) => {
        const aChecked: any =
          filterData.includes(a.id) ||
          a.sub_category?.some((sub) => filterData.includes(sub.id));
        const bChecked: any =
          filterData.includes(b.id) ||
          b.sub_category?.some((sub) => filterData.includes(sub.id));
        return bChecked - aChecked;
      });
    } else {
      displayValues = [...item.values].sort((a, b) => {
        return (
          Number(filterData.includes(b.id)) - Number(filterData.includes(a.id))
        );
      });
    }

    // Custom render for Category
    if (item.name.toLowerCase() === "category") {
      return (
        <>
          <div className="max-h-[320px] overflow-y-auto pr-2 px-4 py-2 custom-scrollbar">
            {displayValues
              .slice(0, showAll ? item.values.length : maxCheckboxesToShow)
              .map((val: ICategory, index) => {
                const isExpanded = expandedCategories.has(val.id);
                const hasSubs = val.sub_category && val.sub_category.length > 0;

                return (
                  <div key={`${item.name}-${val.id}-${index}`} className="mb-4">
                    {/* Category Row */}
                    <div className="flex items-center justify-between mb-2">
                      <Checkbox
                        checked={filterData.includes(val.id)}
                        disabled={checkDisable}
                        sizeClassName="w-5 h-5"
                        labelClassName="text-sm font-normal font-inter flex-1"
                        name={val.name}
                        label={<ToTitleCase text={val.name} />}
                        onChange={(checked) =>
                          handleSelectionChange(item.name, val, checked)
                        }
                      />

                      {hasSubs && (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(val.id)}
                          className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors"
                          aria-expanded={isExpanded}
                          aria-label={`Toggle ${val.name} subcategories`}
                        >
                          <ChevronDown
                            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""
                              }`}
                          />
                        </button>
                      )}
                    </div>

                    {/* Scrollable subcategory section */}
                    {isExpanded && hasSubs && (
                      <div className="ml-6 border-l border-gray-300 pl-4 py-2">
                        <div className="max-h-[180px] overflow-y-auto custom-scrollbar space-y-2 p-1">
                          {val.sub_category.map((sub) => (
                            <div key={sub.id}>
                              <Checkbox
                                checked={filterData.includes(sub.id)}
                                sizeClassName="w-5 h-5"
                                disabled={checkDisable}
                                labelClassName="text-sm font-normal font-inter"
                                name={sub.name}
                                label={<ToTitleCase text={sub.name} />}
                                onChange={(checked) =>
                                  handleSelectionChange(
                                    "subCategory",
                                    { id: sub.id, name: sub.name },
                                    checked
                                  )
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Teaser */}
                    {!isExpanded && hasSubs && (
                      <span className="text-sm text-gray-500 ml-8 block">
                        {val.sub_category.length} sub category
                      </span>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Show all / Hide */}
          {showAll && (
            <span
              className="text-sm text-[#0068FF] block font-semibold cursor-pointer mt-3"
              onClick={onToggleShowAll}
            >
              Hide
            </span>
          )}
          {!showAll && item.values.length > maxCheckboxesToShow && (
            <span
              className="text-sm text-indigo-700 block font-semibold cursor-pointer mt-3"
              onClick={onToggleShowAll}
            >
              Show all
            </span>
          )}
        </>
      );
    }

    return (
      <>
        <div className="max-h-[320px] overflow-y-auto pr-2 px-4 py-2 custom-scrollbar">
          {displayValues
            .slice(0, showAll ? item.values.length : maxCheckboxesToShow)
            .map((val, index) => (
              <div key={`${item.name}-${val.id}-${index}`} className="mb-3">
                <Checkbox
                  checked={filterData.includes(val.id)}
                  sizeClassName="w-5 h-5"
                  labelClassName="text-sm font-normal font-inter"
                  name={val.name}
                  disabled={checkDisable}
                  label={<ToTitleCase text={val.name} />}
                  onChange={(checked) =>
                    handleSelectionChange(item.name, val, checked)
                  }
                />
              </div>
            ))}
        </div>

        {showAll && (
          <span
            className="text-sm text-[#0068FF] block font-semibold cursor-pointer mt-3"
            onClick={onToggleShowAll}
          >
            Hide
          </span>
        )}
        {!showAll && item.values.length > maxCheckboxesToShow && (
          <span
            className="text-sm text-indigo-700 block font-semibold cursor-pointer mt-3"
            onClick={onToggleShowAll}
          >
            Show all
          </span>
        )}
      </>
    );
  };

  const handleFilterRemove = (filterToRemove: string) => {
    const isCategoryId = selectedCategoryIds.includes(filterToRemove);
    const isSubId = selectedSubCategoryIds.includes(filterToRemove);
    const isBrandId = selectedBrandIds.includes(filterToRemove);

    let newSubIds = selectedSubCategoryIds; // Default

    if (isCategoryId) {
      // Remove category and all its subs
      const categoryFacet = facets.find(
        (f) => f.name.toLowerCase() === "category"
      );
      const thisCat = categoryFacet?.values.find(
        (cat) => cat.id === filterToRemove
      ) as ICategory | undefined;
      if (thisCat) {
        const subsToRemove =
          thisCat.sub_category
            ?.map((s) => s.id)
            .filter((id) => selectedSubCategoryIds.includes(id)) || [];
        newSubIds = selectedSubCategoryIds.filter(
          (id) => !subsToRemove.includes(id)
        );
        onSubCategoryChange(newSubIds);
      }
      onCategoryChange(
        selectedCategoryIds.filter((id) => id !== filterToRemove)
      );
    } else if (isSubId) {
      // Remove sub, and check if remove parent
      newSubIds = selectedSubCategoryIds.filter((id) => id !== filterToRemove);
      onSubCategoryChange(newSubIds);

      // Find parent
      const categoryFacet = facets.find(
        (f) => f.name.toLowerCase() === "category"
      );
      const parentCategory = categoryFacet?.values.find((cat) => {
        if (isCategory(cat)) {
          return cat.sub_category?.some((s) => s.id === filterToRemove);
        }
        return false;
      }) as ICategory | undefined;

      if (parentCategory) {
        const parentSubs = parentCategory.sub_category || [];
        const remainingSubs = parentSubs.filter((s) =>
          newSubIds.includes(s.id)
        );
        if (remainingSubs.length === 0) {
          onCategoryChange(
            selectedCategoryIds.filter((id) => id !== parentCategory.id)
          );
        }
      }
    } else if (isBrandId) {
      onBrandChange(selectedBrandIds.filter((id) => id !== filterToRemove));
    } else {
      // Price or other (e.g., custom range)
      const matchingFacet = facets.find(
        (facet) =>
          facet.values.some((val) => val.id === filterToRemove) ||
          (facet.name.toLowerCase() === "price" &&
            /^\d+-\d+$/.test(filterToRemove))
      );

      let value: IFacetValue;
      let facetName: string;

      if (matchingFacet) {
        value = matchingFacet.values.find(
          (val) => val.id === filterToRemove
        ) || {
          id: filterToRemove,
          name: getFilterDisplayName(filterToRemove),
        };
        facetName = matchingFacet.name;
      } else {
        // Fallback for unknown
        facetName = "price"; // Assume price for ranges
        value = {
          id: filterToRemove,
          name: getFilterDisplayName(filterToRemove),
        };
      }

      handleFilter(facetName, value, "remove");
    }
  };

  const getFilterDisplayName = useCallback(
    (filterId: string): string => {
      // Early return if facets not ready (show placeholder)
      if (facets.length === 0) {
        return filterId.includes("-") ? `Price: ₹${filterId}` : "Filter";
      }

      if (filterId.includes("-") && /^\d+-\d+$/.test(filterId)) {
        const [min, max] = filterId.split("-");
        return `₹${min} - ₹${max}`;
      }
      const facetValue = facets
        .flatMap((f) => f.values)
        .find((v) => v.id === filterId);
      if (facetValue?.name) return facetValue.name;

      const categoryFacet = facets.find(
        (f) => f.name.toLowerCase() === "category"
      );
      if (categoryFacet) {
        const sub = categoryFacet.values
          .flatMap((cat: ICategory) => cat.sub_category || [])
          .find((s) => s.id === filterId);
        if (sub?.name) return sub.name;
      }

      return filterId;
    },
    [facets]
  );

  const toggleFacetExpansion = useCallback((facetName: string) => {
    setExpandedFacets((prev) => ({
      ...prev,
      [facetName]: !prev[facetName],
    }));
  }, []);

  const handleCustomPriceSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>, facetName: string) => {
      e.preventDefault();

      if (!validatePriceRange(minPrice, maxPrice)) {
        return;
      }

      const id = `${minPrice}-${maxPrice}`;
      const name = `₹${minPrice} - ₹${maxPrice}`;

      handleSelectionChange(facetName, { id, name }, true);

      setMinPrice("");
      setMaxPrice("");
      setPriceError("");
    },
    [minPrice, maxPrice, handleSelectionChange, validatePriceRange]
  );

  const blockInvalidChars = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="divide-y divide-[#9B9595] ml-1 dark:divide-slate-700 sticky top-10 z-10 px-4 pb-8">
      {showClear && (
        <div className="relative flex flex-col py-8 space-y-4">
          <h3 className="font-semibold mb-2.5">Clear Filters</h3>
          <ButtonTertiary
            variant="cancel"
            sizeClass="sm"
            disabled={checkDisable}
            className="!w-fit self-center"
            onClick={clearFilter}
          >
            <span className="mr-2">Clear Filters</span>
          </ButtonTertiary>

          <div className="flex flex-wrap gap-2 max-w-full">
            {filterData.map((filter, index) => (
              <div
                key={`active-filter-${filter}-${index}`}
                className="flex items-center text-xs font-semibold px-3 py-1 bg-indigo-500 text-white rounded-full"
              >
                <span className="mr-1">{getFilterDisplayName(filter)}</span>
                <button
                  disabled={checkDisable}
                  className="ml-1 text-white hover:text-gray-200 transition-colors"
                  onClick={() => handleFilterRemove(filter)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center">
          <img src={infiniteLoader} alt="Loading..." className="w-[12%]" />
        </div>
      ) : categories ? (
        <div className="text-red-500 text-center py-4">{categories}</div>
      ) : facets.length > 0 ? (
        <React.Fragment key={`filters-${filterData.length}`}>
          {facets.map((item, index) =>
            item.values.length > 0 || item.name.toLowerCase() === "price" ? (
              <div
                key={`facet-${item.name}-${index}`}
                className="relative flex flex-col pb-2 pt-6 space-y-4"
              >
                <h3 className="font-semibold text-xl mb-2.5">
                  {item.name.toLowerCase() === "price"
                    ? `₹ ${item.name}`
                    : item.name}
                </h3>

                {item.name.toLowerCase() === "price" ? (
                  <div className="space-y-4">
                    {item.values.map((val, i) => (
                      <div key={`price-option-${val.id}-${i}`}>
                        <Checkbox
                          checked={filterData.includes(val.id)}
                          sizeClassName="w-5 h-5"
                          labelClassName="text-sm font-normal font-inter"
                          name={val.name}
                          disabled={checkDisable}
                          label={val.name}
                          onChange={(checked) =>
                            handleSelectionChange(item.name, val, checked)
                          }
                        />
                      </div>
                    ))}

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        Custom Range:
                      </p>
                      <form
                        onSubmit={(e) => handleCustomPriceSubmit(e, item.name)}
                        className="flex flex-col justify-center items-center"
                      >
                        <div className="flex gap-2 mb-3">
                          <input
                            type="number"
                            placeholder="Min ₹"
                            value={minPrice}
                            disabled={checkDisable}
                            onKeyDown={blockInvalidChars}
                            onChange={(e) =>
                              handleMinPriceChange(e.target.value)
                            }
                            onWheel={(e) => e.currentTarget.blur()}
                            className={`border rounded px-2 py-1 text-sm w-1/2 focus:outline-none focus:ring-2 transition-colors ${priceError
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:ring-indigo-500"
                              }`}
                          />
                          <input
                            type="number"
                            placeholder="Max ₹"
                            value={maxPrice}
                            maxLength={5}
                            disabled={checkDisable}
                            onKeyDown={blockInvalidChars}
                            onChange={(e) =>
                              handleMaxPriceChange(e.target.value)
                            }
                            onWheel={(e) => e.currentTarget.blur()}
                            className={`border rounded px-2 py-1 text-sm w-1/2 focus:outline-none focus:ring-2 transition-colors ${priceError
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:ring-indigo-500"
                              }`}
                          />
                        </div>

                        {priceError && (
                          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                            <div className="flex items-center">
                              <span className="mr-1">⚠️</span>
                              {priceError}
                            </div>
                          </div>
                        )}

                        <ButtonSecondary
                          type="submit"
                          size="sm"
                          disabled={!minPrice || !maxPrice || !!priceError}
                          className="w-fit !text-sm"
                        >
                          Apply Custom Range
                        </ButtonSecondary>
                      </form>
                    </div>
                  </div>
                ) : (
                  <CheckBoxGroup
                    item={item}
                    showAll={expandedFacets[item.name] || false}
                    onToggleShowAll={() => toggleFacetExpansion(item.name)}
                  />
                )}
              </div>
            ) : null
          )}
        </React.Fragment>
      ) : (
        <div className="text-center py-4 text-gray-500">
          No filters available
        </div>
      )}
    </div>
  );
};

export default DesktopFilterSidebar;
